import type { FC, ReactNode } from "react";
import JsonLdManager from "@/components/JsonLdManager/JsonLdManager";
import MaticPageMeta from "@/components/PageMetaManager/MaticPageMeta";
import DEFAULT_CONFIG from "@/components/PageMetaManager/config";
import SingleBlog from "@/components/Blog/SingleBlog";
import { getPostMeta, type BlogSlug } from "@content/blog";
import { maticMetaTitle } from "@/utils/meta";

interface BlogPostPageProps {
  slug: BlogSlug;
  body: ReactNode;
}

const BlogPostPage: FC<BlogPostPageProps> = ({ slug, body }) => {
  const meta = getPostMeta(slug);
  const blogUrl = `https://maticrobots.com/blog/${meta.slug}`;
  const headerMediaUrl = meta.headerMedia
    ? `https://maticrobots.com${meta.headerMedia}`
    : undefined;

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: meta.title,
      author: { "@type": "Person", name: meta.author },
      datePublished: meta.publishedAt,
      dateModified: meta.updatedAt ?? meta.publishedAt,
      ...(headerMediaUrl ? { image: headerMediaUrl } : {}),
      url: blogUrl,
      publisher: { "@type": "Organization", name: "Matic Robots" },
    },
  ];
  const breadcrumbs = [
    { position: 1, name: "Home", item: "https://maticrobots.com" },
    { position: 2, name: "Blog", item: "https://maticrobots.com/blog" },
    { position: 3, name: meta.title, item: blogUrl },
  ];

  return (
    <>
      <MaticPageMeta
        page="blog"
        config={DEFAULT_CONFIG}
        overrideTitle={meta.seoTitle ?? maticMetaTitle(meta.title)}
        overrideDescription={meta.metaDescription}
        {...(meta.ogImage && {
          overrideImage: {
            type: "image/jpeg",
            width: meta.ogImage.width,
            height: meta.ogImage.height,
            url: `https://maticrobots.com${meta.ogImage.path}`,
          },
        })}
      />
      <JsonLdManager
        structuredData={structuredData}
        breadcrumbs={breadcrumbs}
      />
      <SingleBlog meta={meta} body={body} />
    </>
  );
};

export default BlogPostPage;
